import React, { useState, useEffect } from 'react';
import HomePage from './pages/HomePage';
import PracticePage from './pages/PracticePage';
import TestPage, { checkAnswerCorrect } from './pages/TestPage';
import LeaderboardPage from './pages/LeaderboardPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import WorkInProgressPage from './pages/WorkInProgressPage';
import SubmitScoreModal from './components/SubmitScoreModal';
import DetailedResultsModal from './components/DetailedResultsModal';
import ExitConfirmModal from './components/ExitConfirmModal';
import blogPosts from './blog-posts';
import JobsPage from './pages/JobsPage';
import { jobsData } from './jobs/jobsData';

const API_BASE = process.env.REACT_APP_API_BASE;

const PortfolioMaths = () => {
  // Page navigation
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Test configuration
  const [testType, setTestType] = useState('mental_math');
  const [difficulty, setDifficulty] = useState('medium');
  const [testTypeOpen, setTestTypeOpen] = useState(false);
  const [difficultyOpen, setDifficultyOpen] = useState(false);

  // Test state
  const [currentTest, setCurrentTest] = useState(null);
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300);

  // Results and leaderboard
  const [testResults, setTestResults] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [showSubmitScore, setShowSubmitScore] = useState(false);
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);

  // Blog state
  const [blogPage, setBlogPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);

  // Job filters
  const [jobFilters, setJobFilters] = useState({
    location: '',
    type: '',
    level: ''
  });

  // Demo state
  const [currentProblem, setCurrentProblem] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [problemsAttempted, setProblemsAttempted] = useState(0);
  const [demoProblemIndex, setDemoProblemIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState({});

  const demoProblems = [
    { question: "Calculate: 47 × 23", answer: 1081 },
    { question: "Calculate: 0.15 × 840", answer: 126 },
    { question: "Calculate: 234 + 567", answer: 801 },
    { question: "Calculate: 0.25 x 400?", answer: 100 }
  ];

  // Timer effect
  useEffect(() => {
    if (testStarted && !testCompleted && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && testStarted) {
      finishTest();
    }
  }, [testStarted, testCompleted, timeLeft]);

  // Initialize demo problem
  useEffect(() => {
    if (currentPage === 'home') {
      setCurrentProblem(demoProblems[demoProblemIndex]);
      setUserAnswer('');
      setFeedback(null);
    }
  }, [demoProblemIndex, currentPage]);

  // Load leaderboard when test type or difficulty changes
  useEffect(() => {
    loadLeaderboard();
  }, [testType, difficulty]);

  const checkDemoAnswer = () => {
    if (!currentProblem || !userAnswer) return;
    
    const isCorrect = parseInt(userAnswer) === currentProblem.answer;
    setFeedback({
      correct: isCorrect,
      message: isCorrect ? 'Correct! 🎉' : `Incorrect. Answer: ${currentProblem.answer}`,
      userAnswer: parseInt(userAnswer),
      correctAnswer: currentProblem.answer
    });
    
    setProblemsAttempted(prev => prev + 1);
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    setTimeout(() => {
      setDemoProblemIndex((prev) => (prev + 1) % demoProblems.length);
    }, 1500);
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const endpoint = testType === 'sequences' ? 'sequences/test' : 'mental_math/test';
      const count = testType === 'sequences' ? 25 : 100;
      
      const response = await fetch(`${API_BASE}/${endpoint}?difficulty=${difficulty}&count=${count}`);
      const data = await response.json();
      
      if (testType === 'sequences' || testType === 'mental_math') {
        setCurrentTest(data.problems);
      } else {
        setCurrentTest(data.questions);
      }
      
      setTestStarted(true);
      setTestCompleted(false);
      setCurrentQuestionIndex(0);
      setUserAnswers({});
      setCorrectAnswers({}); // Add this line to reset when starting a new test
      setStartTime(Date.now());
      setTimeLeft(testType === 'sequences' ? 1500 : 600);
    } catch (error) {
      console.error('Error starting test:', error);
    }
    setLoading(false);
  };

  const submitAnswer = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = (finalAnswer = null) => {
    if (currentQuestionIndex < currentTest.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const finalAnswers = finalAnswer 
        ? { ...userAnswers, [currentTest[currentQuestionIndex].id]: finalAnswer }
        : userAnswers;
      finishTest(finalAnswers);
    }
  };

  const evaluateAnswer = (userInput, correctAnswer) => {
    try {
      const cleaned = userInput.trim();
      if (!cleaned) return false;
      
      if (cleaned.includes('/')) {
        const parts = cleaned.split('/');
        if (parts.length !== 2) return false;
        
        const numerator = parseFloat(parts[0]);
        const denominator = parseFloat(parts[1]);
        
        if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
          return false;
        }
        
        const result = numerator / denominator;
        return Math.abs(result - correctAnswer) < 0.0001;
      }
      
      const numValue = parseFloat(cleaned);
      return !isNaN(numValue) && Math.abs(numValue - correctAnswer) < 0.0001;
      
    } catch (error) {
      return false;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const finishTest = (answersToUse = userAnswers) => {
    setTestCompleted(true);
    setTestStarted(false);
    setShowExitConfirm(false);
    let correct = 0;
    const detailedResults = [];
    currentTest.forEach(item => {
      const userAnswer = answersToUse[item.id];  // CHANGE THIS LINE
      let isCorrect = false;
      if (testType === 'sequences' || testType === 'mental_math') {
        isCorrect = checkAnswerCorrect(userAnswer, item.answer);
        if (isCorrect) correct++;
      } else {
        isCorrect = userAnswer && userAnswer.trim().length > 0;
        if (isCorrect) correct++;
      }
      detailedResults.push({
        question: item.question,
        userAnswer: userAnswer || 'No answer',
        correctAnswer: (testType === 'sequences' || testType === 'mental_math') ? item.answer : 'Open-ended',
        isCorrect: isCorrect,
        firmTag: item.firm_tag || null,
        explanation: item.explanation || null
      });
    });
    
    const totalTime = Math.floor((Date.now() - startTime) / 1000);
    const accuracy = (correct / currentTest.length) * 100;
    
    setTestResults({
      correct,
      total: currentTest.length,
      accuracy: Math.round(accuracy),
      timeSpent: totalTime,
      detailedResults
    });
    
    setShowSubmitScore(true);
  };

  const goToLeaderboard = () => {
    loadLeaderboard();
    setCurrentPage('leaderboard');
  };

  const handleExitTest = () => {
    setTestStarted(false);
    setTestCompleted(false);
    setCurrentTest(null);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setCorrectAnswers({});
    setTimeLeft(0);
    setStartTime(0);
    setShowExitConfirm(false);
    setShowSubmitScore(false);
    setTestResults(null);
    setShowDetailedResults(false);
  };

  const submitToLeaderboard = async () => {
    if (!username.trim()) {
      alert('Please enter a username');
      return;
    }
    
    setLoading(true);
    try {
      const submitScore = async (retryCount = 0) => {
        const response = await fetch(`${API_BASE}/leaderboard/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            test_type: testType,
            difficulty: difficulty,
            username: username.trim(),
            score: testResults.correct,
            total_questions: testResults.total,
            time_taken: testResults.timeSpent
          }),
        });

        // If timeout/connection error and first attempt, retry once
        if (!response.ok && retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return submitScore(1);
        }

        return response;
      };

      const response = await submitScore();
      const data = await response.json();
      
      if (response.ok) {
        alert(`Score submitted! Your rank: #${data.rank}`);
        setShowSubmitScore(false);
        goToLeaderboard();
      } else {
        alert('Error submitting score: ' + data.error);
      }
    } catch (error) {
      console.error('Error submitting score:', error);
      alert('Error submitting score');
    }
    setLoading(false);
  };

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/leaderboard/${testType}/${difficulty}`);
      const data = await response.json();
      setLeaderboardData(data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
    setLoading(false);
  };

  const resetTest = () => {
    setCurrentTest(null);
    setTestStarted(false);
    setTestCompleted(false);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setStartTime(null);
    setTimeLeft(300);
    setShowSubmitScore(false);
    setTestResults(null);
    setShowDetailedResults(false);
    setCurrentPage('practice');
  };

  // Render test interface
  if (testStarted && currentTest) {
    return (
      <>
        <TestPage
          currentTest={currentTest}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          userAnswers={userAnswers}
          submitAnswer={submitAnswer}
          nextQuestion={nextQuestion}
          timeLeft={timeLeft}
          difficulty={difficulty}
          testType={testType}
          formatTime={formatTime}
          setShowExitConfirm={setShowExitConfirm}
          correctAnswers={correctAnswers}
          setCorrectAnswers={setCorrectAnswers}
        />
        <ExitConfirmModal
          showExitConfirm={showExitConfirm}
          setShowExitConfirm={setShowExitConfirm}
          handleExitTest={handleExitTest}
        />
      </>
    );
  }

  // Render detailed results modal
  if (showDetailedResults && testResults) {
    return (
      <DetailedResultsModal
        testResults={testResults}
        testType={testType}
        difficulty={difficulty}
        formatTime={formatTime}
        setShowDetailedResults={setShowDetailedResults}
        resetTest={resetTest}
      />
    );
  }

  // Render submit score modal
  if (showSubmitScore && testResults) {
    return (
      <SubmitScoreModal
        testResults={testResults}
        formatTime={formatTime}
        setShowDetailedResults={setShowDetailedResults}
        username={username}
        setUsername={setUsername}
        resetTest={resetTest}
        submitToLeaderboard={submitToLeaderboard}
        loading={loading}
      />
    );
  }

  // Render appropriate page
  const renderPage = () => {
    switch (currentPage) {
      case 'practice':
        return (
          <PracticePage
            testType={testType}
            setTestType={setTestType}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            startTest={startTest}
            loading={loading}
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        );
      case 'leaderboard':
        return (
          <LeaderboardPage
            testType={testType}
            setTestType={setTestType}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            testTypeOpen={testTypeOpen}
            setTestTypeOpen={setTestTypeOpen}
            difficultyOpen={difficultyOpen}
            setDifficultyOpen={setDifficultyOpen}
            leaderboardData={leaderboardData}
            formatTime={formatTime}
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        );
      case 'blog':
        if (selectedPost) {
          return (
            <BlogPostPage
              selectedPost={selectedPost}
              setSelectedPost={setSelectedPost}
              setCurrentPage={setCurrentPage}
              goToLeaderboard={goToLeaderboard}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          );
        }
        return (
          <BlogPage
            blogPage={blogPage}
            setBlogPage={setBlogPage}
            setSelectedPost={setSelectedPost}
            blogPosts={blogPosts}
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        );
      case 'jobs':
        return (
          <JobsPage
            jobs={jobsData}
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            jobFilters={jobFilters}
            setJobFilters={setJobFilters}
          />
        );
      case 'notyetavailable':
        return (
          <WorkInProgressPage
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        );
      default:
        return (
          <HomePage
            currentProblem={currentProblem}
            userAnswer={userAnswer}
            setUserAnswer={setUserAnswer}
            checkDemoAnswer={checkDemoAnswer}
            feedback={feedback}
            score={score}
            problemsAttempted={problemsAttempted}
            setCurrentPage={setCurrentPage}
            goToLeaderboard={goToLeaderboard}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        );
    }
  };

  return (
    <div className="min-h-screen">
      {renderPage()}
    </div>
  );
};

export default PortfolioMaths;